import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Alert, Snackbar } from "@mui/material";

// App-wide toast. Mount <ToastProvider> once at the app root; call
// useToast() anywhere for showSuccess/showError. Styled to match the
// existing per-feature snackbars (filled MUI Alert, top-right, 4s).
const ToastContext = createContext(null);

const AUTO_HIDE_MS = 4000;

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = useCallback((message, severity = "info") => {
    if (!message) return;
    setToast({ open: true, message: String(message), severity });
  }, []);

  const value = useMemo(
    () => ({
      showToast,
      showSuccess: (message) => showToast(message, "success"),
      showError: (message) => showToast(message, "error"),
      showInfo: (message) => showToast(message, "info"),
      showWarning: (message) => showToast(message, "warning"),
    }),
    [showToast],
  );

  const handleClose = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          onClose={handleClose}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
};

export default ToastProvider;
